<!-- Enhanced InternetPointsTable.vue -->
<template>
  <div>
    <!-- Enhanced Search and Filter Row -->
    <div class="row mb-3">
      <div class="col-md-4">
        <div class="search-box">
          <div class="position-relative">
            <input
                type="text"
                class="form-control"
                placeholder="Search by location, provider, or type..."
                v-model="searchQuery"
                @input="handleSearch"
            >
            <i class="bx bx-search-alt search-icon"></i>
          </div>
        </div>
      </div>
      <div class="col-md-8 text-end">
        <!-- Provider Filter -->
        <div class="btn-group me-2">
          <button type="button" class="btn btn-outline-primary btn-sm dropdown-toggle" data-bs-toggle="dropdown">
            <i class="mdi mdi-domain"></i> Provider
            <span v-if="providerFilter" class="badge bg-primary ms-1">{{ providerFilter }}</span>
          </button>
          <div class="dropdown-menu">
            <a class="dropdown-item" href="#" @click.prevent="filterByProvider('')">All Providers</a>
            <div class="dropdown-divider"></div>
            <a class="dropdown-item d-flex align-items-center"
               href="#"
               v-for="provider in uniqueProviders"
               :key="provider"
               @click.prevent="filterByProvider(provider)">
              <img :src="getProviderLogo(provider)" width="20" height="20" class="rounded me-2" :alt="provider">
              {{ provider }}
            </a>
          </div>
        </div>

        <!-- Connection Type Filter -->
        <div class="btn-group me-2">
          <button type="button" class="btn btn-outline-secondary btn-sm dropdown-toggle" data-bs-toggle="dropdown">
            <i class="mdi mdi-router-network"></i> Type
            <span v-if="typeFilter" class="badge bg-secondary ms-1">{{ typeFilter }}</span>
          </button>
          <div class="dropdown-menu">
            <a class="dropdown-item" href="#" @click.prevent="filterByType('')">All Types</a>
            <div class="dropdown-divider"></div>
            <a class="dropdown-item" href="#" @click.prevent="filterByType('Fiber')">
              <i class="mdi mdi-ethernet me-1"></i> Fiber
            </a>
            <a class="dropdown-item" href="#" @click.prevent="filterByType('4G')">
              <i class="mdi mdi-signal-4g me-1"></i> 4G
            </a>
            <a class="dropdown-item" href="#" @click.prevent="filterByType('5G')">
              <i class="mdi mdi-signal-5g me-1"></i> 5G
            </a>
            <a class="dropdown-item" href="#" @click.prevent="filterByType('Wireless')">
              <i class="mdi mdi-wifi me-1"></i> Wireless
            </a>
            <a class="dropdown-item" href="#" @click.prevent="filterByType('Satellite')">
              <i class="mdi mdi-satellite-variant me-1"></i> Satellite
            </a>
          </div>
        </div>

        <!-- Additional Controls -->
        <button class="btn btn-outline-info btn-sm me-2" @click="refreshTable">
          <i class="mdi mdi-refresh"></i> Refresh
        </button>

        <div class="btn-group">
          <button type="button" class="btn btn-outline-success btn-sm dropdown-toggle" data-bs-toggle="dropdown">
            <i class="mdi mdi-export"></i> Export
          </button>
          <div class="dropdown-menu dropdown-menu-end">
            <a class="dropdown-item" href="#" @click.prevent="exportData('csv')">
              <i class="mdi mdi-file-delimited me-1"></i> Export as CSV
            </a>
            <a class="dropdown-item" href="#" @click.prevent="exportData('excel')">
              <i class="mdi mdi-microsoft-excel me-1"></i> Export as Excel
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Enhanced Data Table -->
    <div class="table-responsive">
      <table class="table table-hover table-centered align-middle">
        <thead class="table-light">
        <tr>
          <th style="width: 50px;">
            <div class="form-check">
              <input
                  class="form-check-input"
                  type="checkbox"
                  v-model="selectAll"
                  @change="toggleSelectAll"
              >
            </div>
          </th>
          <th @click="sortBy('name')" class="cursor-pointer">
            Location Name
            <i :class="`mdi mdi-sort-${getSortIcon('name')}`"></i>
          </th>
          <th @click="sortBy('provider')" class="cursor-pointer">
            Service Provider
            <i :class="`mdi mdi-sort-${getSortIcon('provider')}`"></i>
          </th>
          <th @click="sortBy('region')" class="cursor-pointer">
            Region
            <i :class="`mdi mdi-sort-${getSortIcon('region')}`"></i>
          </th>
          <th @click="sortBy('connectionType')" class="cursor-pointer">
            Connection Type
            <i :class="`mdi mdi-sort-${getSortIcon('connectionType')}`"></i>
          </th>
          <th @click="sortBy('speed')" class="cursor-pointer">
            Speed & Performance
            <i :class="`mdi mdi-sort-${getSortIcon('speed')}`"></i>
          </th>
          <th @click="sortBy('cost')" class="cursor-pointer">
            Cost & Value
            <i :class="`mdi mdi-sort-${getSortIcon('cost')}`"></i>
          </th>
          <th>Location</th>
          <th @click="sortBy('dateAdded')" class="cursor-pointer">
            Added
            <i :class="`mdi mdi-sort-${getSortIcon('dateAdded')}`"></i>
          </th>
          <th>Status</th>
          <th class="text-center">Actions</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="point in paginatedPoints" :key="point.id" class="table-row">
          <td>
            <div class="form-check">
              <input
                  class="form-check-input"
                  type="checkbox"
                  v-model="selectedPoints"
                  :value="point.id"
              >
            </div>
          </td>
          <td>
            <div class="d-flex align-items-center">
              <div class="avatar-xs me-2">
                <span class="avatar-title rounded-circle bg-soft-primary text-primary">
                  <i :class="getConnectionIcon(point.connectionType)"></i>
                </span>
              </div>
              <div>
                <h5 class="font-size-14 mb-0">{{ point.name }}</h5>
                <p class="text-muted mb-0 font-size-12">
                  ID: #{{ point.id }}
                  <span v-if="point.packageName" class="ms-1 badge badge-soft-info">{{ point.packageName }}</span>
                </p>
              </div>
            </div>
          </td>
          <td>
            <div class="d-flex align-items-center">
              <img :src="getProviderLogo(point.provider)" width="25" height="25" class="rounded me-2" :alt="point.provider">
              <div>
                <div class="fw-bold" :style="`color: ${getProviderColor(point.provider)}`">
                  {{ point.provider }}
                </div>
                <small class="text-muted">{{ getProviderType(point.provider) }}</small>
              </div>
            </div>
          </td>
          <td>
            <i class="mdi mdi-map-marker text-primary me-1"></i>
            {{ point.region }}
          </td>
          <td>
            <span :class="`badge bg-${getTypeColor(point.connectionType)}`">
              {{ point.connectionType }}
            </span>
          </td>
          <td>
            <div class="speed-performance">
              <div class="d-flex align-items-center mb-1">
                <div class="progress flex-grow-1 me-2" style="height: 8px; max-width: 80px;">
                  <div
                      class="progress-bar"
                      :class="`bg-${getSpeedColor(point.speed)}`"
                      role="progressbar"
                      :style="`width: ${Math.min(100, (point.speed / 1000) * 100)}%`"
                  ></div>
                </div>
                <span class="font-size-12 fw-bold">{{ point.speed }} Mbps</span>
              </div>
              <div v-if="point.reliability" class="d-flex align-items-center">
                <small class="text-muted me-1">Reliability:</small>
                <div class="progress flex-grow-1 me-2" style="height: 4px; max-width: 60px;">
                  <div
                      class="progress-bar"
                      :class="`bg-${getReliabilityColor(point.reliability)}`"
                      :style="`width: ${point.reliability}%`"
                  ></div>
                </div>
                <small class="text-muted">{{ point.reliability }}%</small>
              </div>
            </div>
          </td>
          <td>
            <div v-if="point.cost" class="cost-value">
              <div class="fw-bold text-success">KES {{ formatNumber(point.cost) }}</div>
              <small class="text-muted">per month</small>
              <div v-if="point.rating" class="text-warning small">
                {{ '⭐'.repeat(parseInt(point.rating)) }}
                <span class="text-muted">({{ point.rating }}/5)</span>
              </div>
            </div>
            <span v-else class="text-muted">Not specified</span>
          </td>
          <td>
            <div class="font-size-12">
              <button class="btn btn-sm btn-outline-primary" @click="showOnMap(point)" title="View on Map">
                <i class="mdi mdi-map-marker"></i>
              </button>
              <div class="mt-1">
                <span class="text-muted">{{ parseFloat(point.latitude).toFixed(3) }}, {{ parseFloat(point.longitude).toFixed(3) }}</span>
              </div>
            </div>
          </td>
          <td>
            <div class="font-size-12">
              {{ formatDate(point.dateAdded) }}<br>
              <span class="text-muted">by {{ point.addedBy }}</span>
            </div>
          </td>
          <td>
            <span :class="`badge bg-${point.status === 'active' ? 'success' : 'danger'}`">
              {{ point.status }}
            </span>
          </td>
          <td>
            <div class="d-flex gap-1 justify-content-center">
              <button
                  class="btn btn-sm btn-light"
                  @click="$emit('view', point)"
                  title="View Details"
              >
                <i class="mdi mdi-eye"></i>
              </button>
              <button
                  class="btn btn-sm btn-light"
                  @click="$emit('edit', point)"
                  title="Edit"
              >
                <i class="mdi mdi-pencil text-warning"></i>
              </button>
              <button
                  class="btn btn-sm btn-light"
                  @click="$emit('delete', point)"
                  title="Delete"
              >
                <i class="mdi mdi-delete text-danger"></i>
              </button>
              <div class="btn-group">
                <button class="btn btn-sm btn-light dropdown-toggle" data-bs-toggle="dropdown" title="More Actions">
                  <i class="mdi mdi-dots-vertical"></i>
                </button>
                <div class="dropdown-menu dropdown-menu-end">
                  <a class="dropdown-item" href="#" @click.prevent="duplicatePoint(point)">
                    <i class="mdi mdi-content-duplicate me-1"></i> Duplicate
                  </a>
                  <a class="dropdown-item" href="#" @click.prevent="sharePoint(point)">
                    <i class="mdi mdi-share me-1"></i> Share
                  </a>
                  <div class="dropdown-divider"></div>
                  <a class="dropdown-item" href="#" @click.prevent="generateQR(point)">
                    <i class="mdi mdi-qrcode me-1"></i> Generate QR
                  </a>
                </div>
              </div>
            </div>
          </td>
        </tr>
        <tr v-if="filteredPoints.length === 0">
          <td colspan="11" class="text-center py-4">
            <div class="text-muted">
              <i class="mdi mdi-information-outline" style="font-size: 48px;"></i>
              <p class="mt-2">No internet points found matching your criteria</p>
              <button class="btn btn-primary btn-sm" @click="clearFilters">
                <i class="mdi mdi-filter-remove me-1"></i> Clear Filters
              </button>
            </div>
          </td>
        </tr>
        </tbody>
      </table>
    </div>

    <!-- Enhanced Pagination with Info -->
    <div class="row" v-if="filteredPoints.length > 0">
      <div class="col-sm-12 col-md-5">
        <div class="dataTables_info">
          Showing {{ startIndex + 1 }} to {{ endIndex }} of {{ filteredPoints.length }} entries
          <span v-if="searchQuery || providerFilter || typeFilter" class="text-muted">
            (filtered from {{ points.length }} total entries)
          </span>
        </div>
      </div>
      <div class="col-sm-12 col-md-7">
        <div class="d-flex align-items-center justify-content-md-end">
          <!-- Items per page selector -->
          <div class="me-3">
            <select class="form-select form-select-sm" v-model="itemsPerPage" @change="currentPage = 1">
              <option :value="10">10 per page</option>
              <option :value="25">25 per page</option>
              <option :value="50">50 per page</option>
              <option :value="100">100 per page</option>
            </select>
          </div>

          <!-- Pagination -->
          <div class="dataTables_paginate paging_simple_numbers">
            <ul class="pagination mb-0">
              <li class="paginate_button page-item previous" :class="{ disabled: currentPage === 1 }">
                <a href="#" class="page-link" @click.prevent="currentPage > 1 && currentPage--">
                  <i class="mdi mdi-chevron-left"></i>
                </a>
              </li>
              <li
                  v-for="page in displayPages"
                  :key="page"
                  class="paginate_button page-item"
                  :class="{ active: currentPage === page }"
              >
                <a href="#" class="page-link" @click.prevent="currentPage = page">
                  {{ page }}
                </a>
              </li>
              <li class="paginate_button page-item next" :class="{ disabled: currentPage === totalPages }">
                <a href="#" class="page-link" @click.prevent="currentPage < totalPages && currentPage++">
                  <i class="mdi mdi-chevron-right"></i>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- Bulk Actions (when items are selected) -->
    <div v-if="selectedPoints.length > 0" class="row mt-3">
      <div class="col-12">
        <div class="alert alert-info d-flex align-items-center justify-content-between">
          <div>
            <i class="mdi mdi-information me-2"></i>
            {{ selectedPoints.length }} item(s) selected
          </div>
          <div class="btn-group">
            <button class="btn btn-sm btn-warning" @click="bulkEdit">
              <i class="mdi mdi-pencil me-1"></i> Bulk Edit
            </button>
            <button class="btn btn-sm btn-success" @click="bulkExport">
              <i class="mdi mdi-download me-1"></i> Export Selected
            </button>
            <button class="btn btn-sm btn-danger" @click="bulkDelete">
              <i class="mdi mdi-delete me-1"></i> Delete Selected
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import moment from 'moment';

export default {
  name: 'InternetPointsTable',
  props: {
    points: { type: Array, default: () => [] },
    showProviderColumn: { type: Boolean, default: true }
  },
  data() {
    return {
      searchQuery: '',
      providerFilter: '',
      typeFilter: '',
      selectedPoints: [],
      selectAll: false,
      currentPage: 1,
      itemsPerPage: 25,
      sortColumn: 'dateAdded',
      sortDirection: 'desc',
      providerColors: {
        'Safaricom': '#00AA44',
        'Zuku (Wananchi Group)': '#FF6600',
        'Airtel Kenya': '#FF0000',
        'Telkom Kenya': '#0066CC',
        'JTL Faiba': '#8B008B',
        'Poa Internet': '#00CCCC',
        'Liquid Telecom': '#FF69B4',
        'Mawingu Networks': '#32CD32'
      }
    }
  },
  computed: {
    uniqueProviders() {
      const providers = [...new Set(this.points.map(p => p.provider).filter(Boolean))];
      return providers.sort();
    },

    filteredPoints() {
      let filtered = this.points;

      // Search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(point =>
            point.name?.toLowerCase().includes(query) ||
            point.region?.toLowerCase().includes(query) ||
            point.provider?.toLowerCase().includes(query) ||
            point.connectionType?.toLowerCase().includes(query) ||
            point.notes?.toLowerCase().includes(query) ||
            point.packageName?.toLowerCase().includes(query)
        );
      }

      // Provider filter
      if (this.providerFilter) {
        filtered = filtered.filter(point => point.provider === this.providerFilter);
      }

      // Type filter
      if (this.typeFilter) {
        filtered = filtered.filter(point => point.connectionType === this.typeFilter);
      }

      // Apply sorting
      filtered.sort((a, b) => {
        let aVal = a[this.sortColumn];
        let bVal = b[this.sortColumn];

        // Handle numeric sorting
        if (['speed', 'cost', 'reliability'].includes(this.sortColumn)) {
          aVal = parseFloat(aVal) || 0;
          bVal = parseFloat(bVal) || 0;
        }

        // Handle date sorting
        if (this.sortColumn === 'dateAdded') {
          aVal = new Date(aVal);
          bVal = new Date(bVal);
        }

        if (this.sortDirection === 'asc') {
          return aVal > bVal ? 1 : -1;
        } else {
          return aVal < bVal ? 1 : -1;
        }
      });

      return filtered;
    },

    paginatedPoints() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredPoints.slice(start, end);
    },

    totalPages() {
      return Math.ceil(this.filteredPoints.length / this.itemsPerPage);
    },

    startIndex() {
      return (this.currentPage - 1) * this.itemsPerPage;
    },

    endIndex() {
      return Math.min(this.startIndex + this.itemsPerPage, this.filteredPoints.length);
    },

    displayPages() {
      const pages = [];
      const maxPages = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxPages / 2));
      let end = Math.min(this.totalPages, start + maxPages - 1);

      if (end - start < maxPages - 1) {
        start = Math.max(1, end - maxPages + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      return pages;
    }
  },
  watch: {
    filteredPoints() {
      // Reset to first page when filters change
      this.currentPage = 1;
      // Clear selection when filters change
      this.selectedPoints = [];
      this.selectAll = false;
    }
  },
  methods: {
    formatDate(date) {
      return moment(date).format('MMM DD, YYYY');
    },

    formatNumber(num) {
      return new Intl.NumberFormat().format(num);
    },

    getConnectionIcon(type) {
      const icons = {
        'Fiber': 'mdi mdi-ethernet',
        '4G': 'mdi mdi-signal-4g',
        '5G': 'mdi mdi-signal-5g',
        'Wireless': 'mdi mdi-wifi',
        'Satellite': 'mdi mdi-satellite-variant'
      };
      return icons[type] || 'mdi mdi-router-network';
    },

    getTypeColor(type) {
      const colors = {
        'Fiber': 'primary',
        '4G': 'success',
        '5G': 'info',
        'Wireless': 'warning',
        'Satellite': 'secondary'
      };
      return colors[type] || 'primary';
    },

    getSpeedColor(speed) {
      const speedNum = parseFloat(speed);
      if (speedNum >= 200) return 'success';
      if (speedNum >= 100) return 'info';
      if (speedNum >= 50) return 'warning';
      return 'danger';
    },

    getReliabilityColor(reliability) {
      const rel = parseFloat(reliability);
      if (rel >= 95) return 'success';
      if (rel >= 85) return 'warning';
      return 'danger';
    },

    getProviderColor(provider) {
      return this.providerColors[provider] || '#666666';
    },

    getProviderLogo(provider) {
      const logos = {
        'Safaricom': 'assets/images/providers/safaricom-logo.png',
        'Zuku (Wananchi Group)': 'assets/images/providers/zuku-logo.png',
        'Airtel Kenya': 'assets/images/providers/airtel-logo.png',
        'Telkom Kenya': 'assets/images/providers/telkom-logo.png',
        'JTL Faiba': 'assets/images/providers/faiba-logo.png',
        'Poa Internet': 'assets/images/providers/poa-logo.png',
        'Liquid Telecom': 'assets/images/providers/liquid-logo.png',
        'Mawingu Networks': 'assets/images/providers/mawingu-logo.png'
      };
      return logos[provider] || 'assets/images/providers/safaricom-logo.png';
    },

    getProviderType(provider) {
      const types = {
        'Safaricom': 'Mobile & Fiber',
        'Zuku (Wananchi Group)': 'Cable & Fiber',
        'Airtel Kenya': 'Mobile Network',
        'Telkom Kenya': 'Fixed & Mobile',
        'JTL Faiba': 'Fiber Specialist',
        'Poa Internet': 'Community WiFi',
        'Liquid Telecom': 'Enterprise',
        'Mawingu Networks': 'Wireless ISP'
      };
      return types[provider] || 'ISP';
    },

    getSortIcon(column) {
      if (this.sortColumn !== column) return 'variant';
      return this.sortDirection === 'asc' ? 'ascending' : 'descending';
    },

    sortBy(column) {
      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumn = column;
        this.sortDirection = 'asc';
      }
    },

    handleSearch() {
      this.currentPage = 1;
    },

    filterByProvider(provider) {
      this.providerFilter = provider;
    },

    filterByType(type) {
      this.typeFilter = type;
    },

    clearFilters() {
      this.searchQuery = '';
      this.providerFilter = '';
      this.typeFilter = '';
    },

    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedPoints = this.paginatedPoints.map(p => p.id);
      } else {
        this.selectedPoints = [];
      }
    },

    refreshTable() {
      this.$parent.loadInternetPoints();
      this.selectedPoints = [];
      this.selectAll = false;
    },

    showOnMap(point) {
      // Emit event to parent to show point on map
      this.$emit('showOnMap', point);
    },

    duplicatePoint(point) {
      this.$emit('duplicate', point);
    },

    sharePoint(point) {
      const url = `${window.location.origin}/internet-point/${point.id}`;
      navigator.clipboard.writeText(url).then(() => {
        this.$swal({
          icon: 'success',
          title: 'Link Copied',
          text: 'Internet point link copied to clipboard!',
          timer: 2000
        });
      });
    },

    generateQR(point) {
      // Generate QR code for the point
      this.$swal({
        icon: 'info',
        title: 'QR Code Generated',
        text: 'QR code for this internet point has been generated.',
        timer: 2000
      });
    },

    exportData(format) {
      const data = this.filteredPoints;
      this.$emit('exportData', { format, data });
    },

    bulkEdit() {
      this.$emit('bulkEdit', this.selectedPoints);
    },

    bulkExport() {
      const selectedData = this.filteredPoints.filter(p => this.selectedPoints.includes(p.id));
      this.$emit('exportData', { format: 'csv', data: selectedData });
    },

    bulkDelete() {
      this.$swal({
        title: 'Delete Selected Points?',
        text: `This will delete ${this.selectedPoints.length} internet points.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete them!'
      }).then((result) => {
        if (result.isConfirmed) {
          this.$emit('bulkDelete', this.selectedPoints);
          this.selectedPoints = [];
          this.selectAll = false;
        }
      });
    }
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
  user-select: none;
}

.cursor-pointer:hover {
  color: #0066cc;
}

.search-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #74788d;
}

.avatar-title {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.progress {
  background-color: #f3f3f3;
}

.btn-sm {
  padding: 0.25rem 0.5rem;
}

.table-row {
  transition: background-color 0.2s ease;
}

.table-row:hover {
  background-color: #f8f9fa;
}

.speed-performance, .cost-value {
  min-width: 120px;
}

.fw-bold {
  font-weight: 600 !important;
}

.badge-soft-info {
  color: #0f5397;
  background-color: #e7f3ff;
}

.bg-soft-primary {
  background-color: #e7f3ff;
}

.text-primary {
  color: #0f5397 !important;
}

@media (max-width: 768px) {
  .table-responsive {
    font-size: 0.85rem;
  }

  .btn-sm {
    padding: 0.2rem 0.4rem;
  }

  .progress {
    width: 50px !important;
  }

  .speed-performance, .cost-value {
    min-width: auto;
  }
}
</style>